import { X } from "lucide-react";

export const ANALYTICS_COLORS = {
  orange: "#F75A1B",
  orangeSoft: "#FFEBE3",
  green: "#2F855A",
  greenSoft: "#E6F4EA",
  amber: "#C05621",
  amberSoft: "#FEEBC8",
  red: "#C53030",
  redSoft: "#FED7D7",
  ink: "#1F1F1F",
  muted: "#6B655E",
  line: "#E0D9CD",
  surface: "#FAF7F1",
  blue: "#4C51BF",
  blueSoft: "#EEF2FF",
};

const TONE = {
  neutral: { wrap: "bg-white", icon: "bg-[#F3EFE7] text-[#4A4640]" },
  orange: { wrap: "bg-white", icon: "bg-[#FFEBE3] text-[#F75A1B]" },
  success: { wrap: "bg-white", icon: "bg-[#E6F4EA] text-[#2F855A]" },
  warning: { wrap: "bg-white", icon: "bg-[#FEEBC8] text-[#C05621]" },
  critical: { wrap: "bg-white", icon: "bg-[#FED7D7] text-[#C53030]" },
  info: { wrap: "bg-white", icon: "bg-[#EEF2FF] text-[#4C51BF]" },
};

export function MetricCard({ label, value, hint, icon: Icon, tone = "neutral", onClick, testId }) {
  const t = TONE[tone] || TONE.neutral;
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`doxa-card p-4 sm:p-5 text-left w-full ${t.wrap} ${onClick ? "hover:shadow-md transition-shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/40" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm text-[#6B655E] font-medium">{label}</p>
          <p className="font-display text-2xl sm:text-3xl font-bold mt-1 break-words">{value}</p>
          {hint && <p className="text-xs text-[#6B655E] mt-1 leading-snug">{hint}</p>}
        </div>
        {Icon && <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0 ${t.icon}`}><Icon className="w-4 h-4 sm:w-5 sm:h-5" /></div>}
      </div>
    </Tag>
  );
}

export function ChartCard({ title, subtitle, action, children, className = "", testId }) {
  return (
    <section className={`doxa-card p-4 sm:p-5 ${className}`} data-testid={testId}>
      <header className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h2 className="font-display text-base sm:text-lg font-bold">{title}</h2>
          {subtitle && <p className="text-xs sm:text-sm text-[#6B655E] mt-1 leading-snug">{subtitle}</p>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </header>
      {children}
    </section>
  );
}

export function AnalyticsTabs({ value, onChange, items }) {
  return (
    <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1" role="tablist" aria-label="Visões de analytics">
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={value === item.value}
          onClick={() => onChange(item.value)}
          className={`px-3.5 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${value === item.value ? "bg-[#1F1F1F] text-white" : "bg-white border border-[#E0D9CD] text-[#4A4640] hover:border-[#F75A1B]/50"}`}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

export function EmptyAnalytics({ title = "Sem dados suficientes", description }) {
  return (
    <div className="rounded-xl border border-dashed border-[#D9D1C5] bg-[#FAF7F1] px-4 py-8 text-center">
      <p className="font-medium text-sm">{title}</p>
      {description && <p className="text-xs text-[#6B655E] mt-1 max-w-md mx-auto">{description}</p>}
    </div>
  );
}

export function AnalyticsDrawer({ open, title, subtitle, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex justify-end" onClick={onClose}>
      <div
        className="bg-white w-full sm:max-w-md h-full shadow-2xl p-5 sm:p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <h2 className="font-display text-xl font-bold">{title}</h2>
            {subtitle && <p className="text-sm text-[#6B655E] mt-1">{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-xl hover:bg-[#F6EFE4]" aria-label="Fechar"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

const CELL_STYLE = {
  PRESENT: "bg-[#E6F4EA] text-[#2F855A] border-[#B9DFC7]",
  JUSTIFIED: "bg-[#FEEBC8] text-[#C05621] border-[#F2CE93]",
  ABSENT: "bg-[#FED7D7] text-[#C53030] border-[#F0B6B6]",
};
const CELL_LABEL = { PRESENT: "P", JUSTIFIED: "J", ABSENT: "F" };
const CELL_TITLE = { PRESENT: "Presente", JUSTIFIED: "Falta justificada", ABSENT: "Falta" };

export function AttendanceHeatmap({ lessons = [], students = [], onCellClick }) {
  if (!lessons.length || !students.length) {
    return <EmptyAnalytics description="O mapa aparece quando existem alunos e aulas válidas (abertas/encerradas) para a turma." />;
  }
  return (
    <div className="overflow-auto max-h-[560px] border border-[#EBE5DB] rounded-xl" data-testid="attendance-heatmap">
      <table className="border-separate border-spacing-0 text-xs min-w-max w-full">
        <thead className="sticky top-0 z-20 bg-white">
          <tr>
            <th className="sticky left-0 z-30 bg-white min-w-[170px] px-3 py-2 text-left border-b border-r border-[#EBE5DB] text-[#6B655E] font-medium">Aluno</th>
            {lessons.map((lesson, idx) => (
              <th key={lesson.id} className="w-9 min-w-[36px] px-1 py-2 text-center border-b border-[#EBE5DB] text-[#6B655E] font-medium" title={`${lesson.title}${lesson.lesson_date ? ` · ${lesson.lesson_date}` : ""}`}>
                {idx + 1}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {students.map((student) => (
            <tr key={student.student_id}>
              <td className="sticky left-0 z-10 bg-white px-3 py-2 border-b border-r border-[#F0EBE3] max-w-[220px]">
                <p className="font-medium truncate" title={student.full_name}>{student.full_name}</p>
                <p className="text-[10px] text-[#6B655E]">{student.percent == null ? "Sem dados" : `${student.percent}%`}</p>
              </td>
              {(student.cells || []).map((cell) => (
                <td key={cell.lesson_id} className="p-1 text-center border-b border-[#F0EBE3]">
                  <button
                    type="button"
                    onClick={() => onCellClick?.({ student, lesson: lessons.find((l) => l.id === cell.lesson_id), cell })}
                    className={`w-7 h-7 rounded-md border text-[10px] font-bold ${CELL_STYLE[cell.status] || CELL_STYLE.ABSENT} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#F75A1B]/50`}
                    title={CELL_TITLE[cell.status] || "Falta"}
                    aria-label={`${student.full_name}: ${CELL_TITLE[cell.status] || "Falta"}`}
                  >
                    {CELL_LABEL[cell.status] || "F"}
                  </button>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="sticky bottom-0 left-0 bg-white border-t border-[#EBE5DB] px-3 py-2 flex gap-3 text-[11px] text-[#6B655E]">
        <span><strong className="text-[#2F855A]">P</strong> Presente</span>
        <span><strong className="text-[#C05621]">J</strong> Justificada</span>
        <span><strong className="text-[#C53030]">F</strong> Falta</span>
      </div>
    </div>
  );
}

export function formatPercent(value) {
  return value == null ? "Sem dados" : `${Math.round(value)}%`;
}
